import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { FormField } from '@/components/ui/FormField';
import { Button } from '@/components/ui/button';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ChannelConfig {
  webhookUrl: string;
  active: boolean;
}

interface Configs {
  amazon: ChannelConfig;
  fnac: ChannelConfig;
  worten: ChannelConfig;
  kuantokusta: ChannelConfig;
}

export default function ChannelSettings() {
  const [configs, setConfigs] = useState<Configs>({
    amazon: { webhookUrl: '', active: false },
    fnac: { webhookUrl: '', active: false },
    worten: { webhookUrl: '', active: false },
    kuantokusta: { webhookUrl: '', active: false },
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const docRef = doc(db, 'config', 'multicanal');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setConfigs(prevConfigs => ({
          amazon: { ...prevConfigs.amazon, ...data.amazon, webhookUrl: data.amazon?.webhookUrl || '' },
          fnac: { ...prevConfigs.fnac, ...data.fnac, webhookUrl: data.fnac?.webhookUrl || '' },
          worten: { ...prevConfigs.worten, ...data.worten, webhookUrl: data.worten?.webhookUrl || '' },
          kuantokusta: { ...prevConfigs.kuantokusta, ...data.kuantokusta, webhookUrl: data.kuantokusta?.webhookUrl || '' },
        }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Erro ao carregar configurações');
    }
  };

  const handleInputChange = (channel: string, value: string) => {
    setConfigs(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        webhookUrl: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      const docRef = doc(db, 'config', 'multicanal');
      await setDoc(docRef, configs, { merge: true });
      toast.success('Configurações salvas com sucesso');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configurações');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Configurações de Multicanal</h1>
      </div>
      
      <Card>
        <CardHeader>
          <h2 className="text-3xl font-bold tracking-tight">Configurações dos Canais</h2>
        </CardHeader>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">Amazon</h2>
          <div className="space-y-4">
            <FormField
              label="URL funcionamento da Amazon"
              type="url"
              value={configs.amazon.webhookUrl || ''}
              onChange={(e) => handleInputChange('amazon', e.target.value)}
              helperText="Endpoint para integração com a Amazon"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold mb-4">Fnac</h2>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <FormField
              label="URL funcionamento da Fnac"
              type="url"
              value={configs.fnac.webhookUrl || ''}
              onChange={(e) => handleInputChange('fnac', e.target.value)}
              helperText="Endpoint para integração com a Fnac"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold mb-4">Worten</h2>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <FormField
              label="URL funcionamento da Worten"
              type="url"
              value={configs.worten.webhookUrl || ''}
              onChange={(e) => handleInputChange('worten', e.target.value)}
              helperText="Endpoint para integração com a Worten"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold mb-4">KuantoKusta</h2>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <FormField
              label="URL funcionamento do KuantoKusta"
              type="url"
              value={configs.kuantokusta.webhookUrl || ''}
              onChange={(e) => handleInputChange('kuantokusta', e.target.value)}
              helperText="Endpoint para integração com o KuantoKusta"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="w-full md:w-auto">
          Salvar Configurações
        </Button>
      </div>
    </div>
  );
}
